import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui';
import { toLocalInputValue } from '@/features/interviews/status';
import { errorMessage } from '@/hooks/useApiError';

/** Tomorrow at 10:00, so the form opens on a plausible slot rather than blank. */
function defaultSlot() {
  const when = new Date();
  when.setDate(when.getDate() + 1);
  when.setHours(10, 0, 0, 0);
  return toLocalInputValue(when);
}

const EMPTY = {
  title: 'First-round interview',
  mode: 'VIDEO',
  scheduled_at: defaultSlot(),
  duration_minutes: 45,
  location: '',
  meeting_url: '',
  instructions: '',
};

/**
 * Books a new interview, or moves an existing one when `interview` is given.
 * The two share a form because they ask the same questions.
 *
 * `acceptRequest` moves it as the answer to the candidate's request for
 * another time (the accept endpoint, which also closes the request);
 * `initialScheduledAt` opens the form on that time.
 */
export function ScheduleDialog({
  open,
  onClose,
  applicationId,
  interview = null,
  roundNumber = null,
  acceptRequest = false,
  initialScheduledAt = null,
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isReschedule = Boolean(interview);
  const [form, setForm] = useState(() =>
    interview
      ? {
          title: interview.title,
          mode: interview.mode,
          scheduled_at: toLocalInputValue(new Date(initialScheduledAt ?? interview.scheduled_at)),
          duration_minutes: interview.duration_minutes,
          location: interview.location ?? '',
          meeting_url: interview.meeting_url ?? '',
          instructions: interview.instructions ?? '',
          reason: '',
        }
      : {
          ...EMPTY,
          title: roundNumber > 1 ? `Round ${roundNumber} interview` : EMPTY.title,
        },
  );

  const save = useMutation({
    mutationFn: (values) =>
      acceptRequest
        ? companyApi.acceptRescheduleRequest(interview.id, values)
        : isReschedule
          ? companyApi.rescheduleInterview(interview.id, values)
          : companyApi.scheduleInterview({ ...values, application_id: applicationId }),
    onSuccess: () => {
      toast.success(
        acceptRequest
          ? 'Interview moved. The candidate has been told their request was accepted.'
          : isReschedule
            ? 'Interview updated'
            : 'Interview scheduled',
      );
      void queryClient.invalidateQueries({ queryKey: ['company-interviews'] });
      void queryClient.invalidateQueries({ queryKey: ['interview-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['company-application'] });
      void queryClient.invalidateQueries({ queryKey: ['company-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['company-application-counts'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save the interview')),
  });

  const [timeError, setTimeError] = useState(null);

  function update(field, value) {
    if (field === 'scheduled_at') setTimeError(null);
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submit(event) {
    event.preventDefault();
    // The server refuses a time that has passed; say so before asking it.
    if (new Date(form.scheduled_at) <= new Date()) {
      setTimeError('This time has already passed. Choose a later date and time.');
      return;
    }
    const payload = {
      title: form.title,
      mode: form.mode,
      // The input is local time; the API wants an absolute instant.
      scheduled_at: new Date(form.scheduled_at).toISOString(),
      duration_minutes: Number(form.duration_minutes),
      instructions: form.instructions || null,
      location: form.mode === 'ONSITE' ? form.location : null,
      meeting_url: form.mode === 'VIDEO' ? form.meeting_url : null,
      ...(isReschedule && form.reason ? { reason: form.reason } : {}),
    };
    if (isReschedule) delete payload.title;
    save.mutate(payload);
  }

  return (
    <Modal
      open={open}
      title={
        acceptRequest
          ? 'Accept the request: choose the new time'
          : isReschedule
            ? `Change "${interview.title}"`
            : roundNumber
              ? `Schedule round ${roundNumber}`
              : 'Schedule an interview'
      }
      description={
        acceptRequest
          ? 'The interview moves to this time and the candidate is told their request was accepted.'
          : isReschedule
            ? 'A new time asks the candidate to confirm again. A new link, address or instructions do not.'
            : 'The candidate sees the time, the joining details and your instructions.'
      }
      onClose={onClose}
    >
      <form className="space-y-4" onSubmit={submit}>
        {!isReschedule && (
          <Input
            label="Title"
            required
            value={form.title}
            onChange={(event) => update('title', event.target.value)}
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Format"
            value={form.mode}
            onChange={(event) => update('mode', event.target.value)}
          >
            <option value="VIDEO">Video call</option>
            <option value="PHONE">Phone call</option>
            <option value="ONSITE">On site</option>
          </Select>
          <Input
            label="Length"
            type="number"
            min={5}
            max={480}
            step={5}
            hint="Minutes"
            value={form.duration_minutes}
            onChange={(event) => update('duration_minutes', event.target.value)}
          />
        </div>

        <Input
          label="Starts"
          type="datetime-local"
          required
          hint="Your local time."
          // Past days and times are greyed out in the picker.
          min={toLocalInputValue(new Date())}
          error={timeError}
          value={form.scheduled_at}
          onChange={(event) => update('scheduled_at', event.target.value)}
        />

        {form.mode === 'VIDEO' && (
          <Input
            label="Meeting link"
            type="url"
            required
            placeholder="https://meet.example.com/abc-defg-hij"
            hint="Sent to the candidate in the invitation and in each reminder."
            value={form.meeting_url}
            onChange={(event) => update('meeting_url', event.target.value)}
          />
        )}
        {form.mode === 'ONSITE' && (
          <Input
            label="Address"
            required
            placeholder="Keizersgracht 1, Amsterdam — 3rd floor reception"
            value={form.location}
            onChange={(event) => update('location', event.target.value)}
          />
        )}

        <Textarea
          label="Instructions for the candidate"
          rows={3}
          value={form.instructions}
          onChange={(event) => update('instructions', event.target.value)}
        />

        {isReschedule && (
          <Input
            label={acceptRequest ? 'Message to the candidate (optional)' : 'Why the change?'}
            hint="Shown to the candidate."
            value={form.reason}
            onChange={(event) => update('reason', event.target.value)}
          />
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={save.isPending}>
            {acceptRequest ? 'Accept and move' : isReschedule ? 'Save changes' : 'Schedule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
